import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Chip,
  CircularProgress,
  Pagination as MuiPagination,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Card,
  CardContent,
  CardActions,
  Grid,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LinkIcon from '@mui/icons-material/Link';
import ListPageLayout from '../components/shared/list-page-layout';
import ListFilters, { FilterField } from '../components/shared/list-filters';
import { CardGridSkeleton } from '../components/shared/skeletons';
import { IListGovernmentProjectsRequest } from '../../model/base/request/list-government-projects-request';
import { useGovernmentProjectsController } from './context/government-projects-controller';
import { useGovernmentProjectsState } from './context/government-projects-context';
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";

export function GovernmentProjectsComponent() {
  const { navigateTo: navigate } = useRoutedNavigation();
  const { loadData } = useGovernmentProjectsController();
  const { projects, loading } = useGovernmentProjectsState();
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<IListGovernmentProjectsRequest>({
    page: 0,
    pageSize: 12,
    term: '',
    projectTypeId: undefined,
  });

  useEffect(() => {
    loadData(filters);
  }, []);

  const filterFields: FilterField[] = [
    {
      name: 'term',
      label: 'Pesquisar',
      type: 'text',
      placeholder: 'Buscar por nome do projeto ou responsável',
      fullWidth: true,
    },
  ];

  const handleFilterChange = (name: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    const searchFilters = { ...filters, page: 0 };
    setCurrentPage(1);
    setFilters(searchFilters);
    loadData(searchFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: IListGovernmentProjectsRequest = {
      page: 0,
      pageSize: 12,
      term: '',
      projectTypeId: undefined,
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    loadData(clearedFilters);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    const newFilters = { ...filters, page: page - 1 };
    setFilters(newFilters);
    loadData(newFilters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewProject = (id: number) => {
    navigate(`/projetos-governamentais/detalhes/${id}`);
  };

  const handleOpenLink = (link?: string) => {
    if (link) {
      window.open(link, '_blank');
    }
  };

  return (
    <ListPageLayout
      title="Projetos Governamentais"
      subtitle="Conheça os projetos em desenvolvimento no município"
      breadcrumbs={[
        { label: 'Projetos Governamentais' },
      ]}
      filters={
        <ListFilters
          fields={filterFields.map((field) => ({
            ...field,
            value: filters[field.name as keyof IListGovernmentProjectsRequest],
          }))}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={loading}
        />
      }
    >
      {loading && !projects.hasItems() ? (
        <CardGridSkeleton items={12} hasImage={false} hasActions={true} />
      ) : !projects.hasItems() ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nenhum projeto encontrado com os filtros aplicados.
        </Alert>
      ) : (
        <>
          <Box sx={{ position: 'relative' }}>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1000,
                  borderRadius: 2,
                }}
              >
                <CircularProgress size={60} />
              </Box>
            )}
            <Grid
              container
              spacing={3}
              sx={{
                opacity: loading ? 0.5 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              {projects.items.map((project) => (
                <Grid item xs={12} sm={6} md={4} key={project.id}>
                  <Card
                    elevation={3}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <AccountTreeIcon color="primary" />
                        <Typography
                          variant="h6"
                          component="h3"
                          sx={{
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.3,
                          }}
                        >
                          {project.projectName}
                        </Typography>
                      </Box>

                      {project.responsibleName && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            Responsável:
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            {project.responsibleName}
                          </Typography>
                        </Box>
                      )}

                      {project.results && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: 1.5,
                            }}
                          >
                            {project.results}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>

                    <CardActions
                      sx={{
                        justifyContent: 'space-between',
                        px: 2,
                        pb: 2,
                      }}
                    >
                      <Tooltip title="Ver Detalhes" arrow>
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleViewProject(project.id)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'info.main',
                              color: 'white',
                            },
                          }}
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                      {project.link && (
                        <Tooltip title="Acessar Link Externo" arrow>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenLink(project.link)}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'primary.main',
                                color: 'white',
                              },
                            }}
                          >
                            <LinkIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {projects.pageCount > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 4,
                mb: 2,
              }}
            >
              <MuiPagination
                count={projects.pageCount}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                disabled={loading}
              />
            </Box>
          )}
        </>
      )}
    </ListPageLayout>
  );
}
